import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Upload, Maximize, Scan } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getFileUrl } from '../../lib/api';

export default function SliderManager() {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', subtitle: '', link: '', button_text: '', fit_mode: 'contain', position: 0, is_active: true });
  const [imageFile, setImageFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchSliders = useCallback(() => {
    api.get('/sliders').then(r => setSliders(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchSliders(); }, [fetchSliders]);

  const resetForm = () => {
    setForm({ title: '', subtitle: '', link: '', button_text: '', fit_mode: 'contain', position: 0, is_active: true });
    setImageFile(null);
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (slider) => {
    setEditing(slider);
    setForm({
      title: slider.title || '',
      subtitle: slider.subtitle || '',
      link: slider.link || '',
      button_text: slider.button_text || '',
      fit_mode: slider.fit_mode || 'contain',
      position: slider.position || 0,
      is_active: slider.is_active !== false
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      let imagePath = editing?.image_path || '';
      if (imageFile) {
        const fd = new FormData();
        fd.append('file', imageFile);
        const uploadRes = await api.post('/uploads', fd);
        imagePath = uploadRes.data.path;
      }

      const payload = { ...form, image_path: imagePath };
      if (editing) {
        await api.put(`/sliders/${editing.id}`, payload);
        toast.success('Slider güncellendi');
      } else {
        await api.post('/sliders', payload);
        toast.success('Slider eklendi');
      }
      resetForm();
      fetchSliders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/sliders/${id}`);
      toast.success('Slider silindi');
      fetchSliders();
    } catch (err) {
      toast.error('Silme hatası');
    }
  };

  return (
    <div data-testid="admin-slider-manager">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-semibold">Slider Yönetimi</h1>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)] active:scale-[0.98] transition-colors"
          data-testid="admin-slider-create-button"
        >
          <Plus className="w-4 h-4" /> Yeni Slider
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="mb-8 p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4">
            {editing ? 'Slider Düzenle' : 'Yeni Slider Ekle'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Başlık" value={form.title} onChange={v => setForm(f => ({ ...f, title: v }))} />
              <InputField label="Alt Başlık" value={form.subtitle} onChange={v => setForm(f => ({ ...f, subtitle: v }))} />
              <InputField label="Link" value={form.link} onChange={v => setForm(f => ({ ...f, link: v }))} placeholder="/proje/proje-adi" />
              <InputField label="Sıra" type="number" value={form.position} onChange={v => setForm(f => ({ ...f, position: parseInt(v) || 0 }))} />
            </div>

            {/* Button text - only relevant when a link is provided */}
            {form.link && form.link.trim() && (
              <div className="max-w-md">
                <InputField
                  label="Buton Metni"
                  value={form.button_text}
                  onChange={v => setForm(f => ({ ...f, button_text: v }))}
                  placeholder="Projeyi İncele (varsayılan)"
                />
                <p className="text-xs text-muted-foreground mt-1">Boş bırakılırsa buton "Projeyi İncele" olarak görünür.</p>
              </div>
            )}

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium mb-1.5">Gorsel</label>
              <div className="flex items-center gap-4">
                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-input bg-background text-sm hover:bg-secondary transition-colors">
                  <Upload className="w-4 h-4" /> Görsel Seç
                  <input type="file" accept="image/*" className="hidden" onChange={e => setImageFile(e.target.files[0])} />
                </label>
                <span className="text-sm text-muted-foreground">
                  {imageFile ? imageFile.name : (editing?.image_path ? 'Mevcut görsel yüklü' : 'Görsel seçilmedi')}
                </span>
              </div>
              {editing?.image_path && (
                <img src={getFileUrl(editing.image_path)} alt="" className="mt-2 h-20 rounded-lg object-cover" />
              )}
            </div>

            {/* Görsel Sığdırma (fit mode) */}
            <div>
              <label className="block text-sm font-medium mb-1.5">Görsel Sığdırma</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, fit_mode: 'contain' }))}
                  className={`flex items-start gap-3 p-3 rounded-xl border text-left transition-colors ${form.fit_mode === 'contain' ? 'border-[hsl(var(--gold))] bg-[hsl(var(--gold)/0.08)]' : 'border-input hover:bg-secondary'}`}
                  data-testid="slider-fit-contain"
                >
                  <Scan className={`w-5 h-5 mt-0.5 shrink-0 ${form.fit_mode === 'contain' ? 'text-[hsl(var(--gold))]' : 'text-muted-foreground'}`} />
                  <span>
                    <span className="block text-sm font-medium">Tümünü göster</span>
                    <span className="block text-xs text-muted-foreground">Görselin tamamı görünür, kırpılmaz (afiş/logolu görseller için önerilir).</span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, fit_mode: 'cover' }))}
                  className={`flex items-start gap-3 p-3 rounded-xl border text-left transition-colors ${form.fit_mode === 'cover' ? 'border-[hsl(var(--gold))] bg-[hsl(var(--gold)/0.08)]' : 'border-input hover:bg-secondary'}`}
                  data-testid="slider-fit-cover"
                >
                  <Maximize className={`w-5 h-5 mt-0.5 shrink-0 ${form.fit_mode === 'cover' ? 'text-[hsl(var(--gold))]' : 'text-muted-foreground'}`} />
                  <span>
                    <span className="block text-sm font-medium">Doldur (kırp)</span>
                    <span className="block text-xs text-muted-foreground">Alanı tamamen doldurur; kenarlardan kırpılabilir (tam ekran foto için).</span>
                  </span>
                </button>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} className="rounded" />
                Aktif
              </label>
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)] disabled:opacity-50"
                data-testid="admin-save-button"
              >
                {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
              <button type="button" onClick={resetForm} className="px-6 py-2.5 rounded-lg border border-input text-sm hover:bg-secondary">
                İptal
              </button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
      ) : sliders.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">Henüz slider eklenmemiş</div>
      ) : (
        <div className="space-y-3">
          {sliders.map(slider => (
            <div key={slider.id} className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border">
              <div className="w-24 h-16 rounded-lg overflow-hidden bg-secondary shrink-0">
                {slider.image_path && <img src={getFileUrl(slider.image_path)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{slider.title || 'Başlıksız'}</div>
                <div className="text-xs text-muted-foreground">{slider.subtitle}</div>
              </div>
              <span className={`px-2 py-0.5 text-xs rounded-full ${slider.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                {slider.is_active ? 'Aktif' : 'Pasif'}
              </span>
              <div className="flex gap-1">
                <button onClick={() => handleEdit(slider)} className="p-2 rounded-lg hover:bg-secondary"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(slider.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const InputField = ({ label, value, onChange, type = 'text', placeholder }) => (
  <div>
    <label className="block text-sm font-medium mb-1.5">{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
    />
  </div>
);
